import { IHeaderOption } from 'props';
import { useMemo, useState } from 'react';
import { RiMenuFill } from 'react-icons/ri';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IAddressViewModel } from 'viewModels';
import { ReactComponent as Logout } from '../../assets/icons/iconLogout.svg';
import LogoVigna from '../../assets/images/logoHUB.png';
import LogoAdv from '../../assets/images/logoHUB-adv.png';
import { ReactComponent as LogoHub2 } from '../../assets/icons/logoHUB_2.svg';
import { useAuth } from '../../context';
import { TypeEvent } from '../../pages/Schedule/Event';
import { ProfileType, TypeUser } from '../../types/enums';
import { IAttachment } from '../Attachment';
import { Button } from '../Buttons/Button';
import { MenuMobile } from '../MenuMobile';
import { NotificationHeader } from '../NotificationHeader';
import { OptionsProfile } from '../OptionsProfile';
import { Container, Content } from './styles';
import { useConfig } from '../../context/configCustom';
import { UserController } from '../../controller/UserController';
import { useAlert } from '../../hooks/useAlert';

interface HeaderProps {
  typeHeader:
  | 'default'
  | 'loggedOut'
  | 'company'
  | 'user'
  | 'collaborator'
  | 'plaintiff'
  | 'provider';
}

export type IEvent = {
  id: string;
  date: Date;
  type: TypeEvent;
  title?: string;
  description?: string;
  conference?: string;
  attachments?: IAttachment[];
  place?: IAddressViewModel;
  value?: string;
  note?: string;
};

export function NavHeader({ typeHeader }: HeaderProps) {
  const { pathResourceEmail } = useConfig();
  const alert = useAlert();
  const { user, tokenIsValid, logout } = useAuth();
  const navigate = useNavigate();
  const pathName = useLocation().pathname.split('/')?.[1];
  const [activeOption, setActiveOption] = useState<string>();
  const [activeSubOption, setActiveSubOption] = useState<string>();

  const routes: IHeaderOption[] = useMemo(
    () => [
      {
        name: 'Agenda',
        path: '/schedule',
      },
      {
        name: 'Empresa',
        path: '/company',
        enabled: [TypeUser.COMPANY, TypeUser.ADM].includes(user?.type),
        options: [
          {
            path: '/collaborator',
            name: 'Colaboradores',
            description:
              'Os colaboradores são os funcionários da sua empresa, a partir desse menu é possível adicionar novos colaboradores, editar ou visualizar os colaboradores já cadastrados.',
          },
          {
            path: '/clients',
            name: 'Clientes',
            description:
              'Os clientes são os usuários dos serviços da sua empresa, a partir desse menu é possível adicionar novos clientes, editar ou visualizar os clientes já cadastrados.',
          },
          {
            path: '/courts',
            name: 'Varas e Juizados',
            description:
              'As varas e juizados referem-se a uma divisão ou departamento dentro do sistema judiciário que lida com casos específicos. Cada vara ou juizado tem sua jurisdição sobre um tipo particular de caso ou assunto legal, a partir desse menu é possível adicionar ou visualizar as varas e juizados já cadastradas.',
          },
          {
            path: '/branch',
            name: 'Filiais',
            description:
              'As filiais são as novas unidades da sua empresa, que seguem as mesmas regras e diretrizes, a partir desse menu é possível adicionar novas filiais, editar ou visualizar as filiais já cadastradas.',
          },
          {
            path: '/department',
            name: 'Departamentos',
            description:
              'Os departamentos correspondem as divisões da sua empresa, a partir desse menu é possível adicionar novos departamentos, editar ou visualizar os departamentos já cadastrados.',
          },
          {
            path: '/profiles',
            name: 'Perfis',
            description:
              'Os perfis correspondem aos acessos e preferências que o seu colaborador pode ter dentro da sua empresa, a partir desse menu é possível adicionar novos perfis, editar ou visualizar os perfis já cadastrados.',
          },
          // {
          //   path: '/correspondence',
          //   name: 'Serviços de correspondência',
          //   description:
          //     'Os serviços de correspondência são os tipos de demandas que sua empresa faz, a partir desse menu é possível adicionar novos serviços, excluir ou visualizar os serviços já cadastrados.',
          // },
          // {
          //   path: '/specialties',
          //   name: 'Especialidades',
          //   description:
          //     'As especialidades são os tipos de direito que sua empresa atende, a partir desse menu é possível adicionar novas especialidades, excluir ou visualizar as especialidades já cadastradas.',
          // },
        ],
      },
      {
        name: 'Solicitação',
        path: '/solicitation',
        enabled: true,
        options: [
          {
            path: '/clients',
            name: 'Clientes',
            enabled: user.type === TypeUser.USER,
            description:
              'Os clientes são os usuários dos seus serviços, a partir desse menu é possível adicionar novos clientes, editar ou visualizar os clientes já cadastrados.',
          },
          {
            path: '/solicitation',
            enabled:
              !user?.collaboratorInfo ||
              user?.collaboratorInfo?.profileType === ProfileType.REQUESTER,
            name: 'Abrir solicitação',
            description:
              'As solicitações são as diligências que serão abertas para os correspondentes. A partir desse menu é possível abrir novas solicitações, com as devidas informações e selecionar os correspondentes desejados para prestar esse serviço.',
          },
          {
            path: '/courts',
            name: 'Adicionar varas e juizados',
            enabled: user?.collaboratorInfo?.profileType === ProfileType.OPERATOR,
            description:
              'As varas e juizados referem-se a uma divisão ou departamento dentro do sistema judiciário que lida com casos específicos. Cada vara ou juizado tem sua jurisdição sobre um tipo particular de caso ou assunto legal, a partir desse menu é possível adicionar ou visualizar as varas e juizados já cadastradas.',
          },
          {
            path: '/yoursolicitations',
            name: 'Solicitações geradas',
            description:
              'As solicitações geradas são as diligências que já foram abertas para os correspondentes. A partir desse menu é possível fazer alterações e adicionar novas informações nas solicitações já abertas, acompanhar os status das solicitações, falar diretamente com os correspondentes a partir do chat, visualizar o histórico das solicitações, utilizar filtros de busca e extrair relatório das solicitações exibidas de acordo com o filtro.',
          },
        ],
      },
      {
        name: 'Suas demandas',
        path: '/yourdemands',
        enabled: user?.type !== TypeUser.COLLABORATOR,
      },
      {
        name: 'Relatório',
        path: '/financial',
        enabled:
          !user?.collaboratorInfo || user?.collaboratorInfo?.profileType === ProfileType.FINANCIAL,
        // options: [
        // {
        //   enabled: false,
        //   path: '/dashboard',
        //   name: 'Dashboard',
        //   description:
        //     'Os dashboards são relatórios que contém informações para analises dos administradores do sistema. A partir desse menu é possível extrair esses relatórios em excel de acordo com as datas selecionadas.',
        // },
        // {
        //   path: '/financial',
        //   name: 'Financeiro',
        //   description:
        //     'O financeiro mostra todos os dados de pagamento das diligências. A partir desse menu é possível alterar status de pagamentos, fazendo um a um ou selecionando vários através da opção de multi pagamentos, e extrair relatórios de acordo com os filtros aplicados.',
        // },
        //],
      },
      {
        name: 'Contatos',
        path: '/friends',
        enabled: false,
      },
      {
        name: 'Cliente',
        path: '/registerClient',
        enabled: user.type === TypeUser.ADM,
      },
      {
        name: 'Dashboard',
        path: '/dashboard',
        enabled: user?.type === TypeUser.ADM,
      },
    ],
    [user?.collaboratorInfo, user.type],
  );

  const subOptions = useMemo(
    () => routes.find((r) => r.path === activeOption)?.options?.filter((o) => o.enabled !== false),
    [activeOption, routes],
  );

  const handleMouseLeaveContainerPath = () => {
    setActiveOption(undefined);
    setActiveSubOption(undefined);
  };

  const handleLogout = () => logout();

  const [menuIsVisible, setMenuIsVisible] = useState(false);

  const handleRedirectTraining = async () => {
    await UserController.legalTrainingSigin()
      .then((res) => {
        const urlRedirect = new URL(res);
        window.open(urlRedirect, '_blank');
      })
      .catch(() => {
        alert.show({
          message: 'Não foi possível realizar o redirecionamento para o treinamento',
          type: 'error',
        });
      });
  };

  const handleRedirectTimeSheet = async () => {
    await UserController.timeSheetSigin()
      .then((res) => {
        const urlRedirect = new URL(res);
        window.open(urlRedirect, '_blank');
      })
      .catch(() => {
        alert.show({
          message: 'Não foi possível realizar o redirecionamento para o timesheet',
          type: 'error',
        });
      });
  };
  return (
    <Container>
      <Content typeHeader={typeHeader}>
        <button
          type="button"
          onClick={() => navigate(tokenIsValid ? '/feed' : '/')}
          className="logo">
          <img
            src={pathResourceEmail === 'TemplatesVigna' ? LogoVigna : LogoAdv}
            className="logo"
            alt="logo do legal hub"
          />
        </button>

        <RiMenuFill className="mobile" onClick={() => setMenuIsVisible(true)} />
        <MenuMobile
          menuIsVisible={menuIsVisible}
          setMenuIsVisible={setMenuIsVisible}
          typeHeader={typeHeader}
        />
        {typeHeader === 'default' ? (
          tokenIsValid && (
            <Button
              text=""
              LeftComponent={Logout}
              style={{ fontSize: '1.20rem', display: 'flex', alignItems: 'center' }}
              dimensions={{
                width: '2rem',
                height: '2rem',
              }}
              positionLeftElement={10}
              border={{ color: 'transparent', width: 0 }}
              color="background_white"
              textHoverColor="background_white"
              backgroundHoverColor="primary"
              onClick={handleLogout}
            />
          )
        ) : typeHeader === 'loggedOut' || !tokenIsValid ? (
          <div className="containerLoggedOut">
            <div className="containerLoggedOutItens">
              {!tokenIsValid && (
                <div className="textRegister">
                  <Button
                    text="Login"
                    dimensions={{ width: '12rem', height: '2rem' }}
                    color="background_white"
                    backgroundHoverColor="secondary"
                    border={{ color: 'transparent', width: 1 }}
                    background="tertiary_500"
                    textHoverColor="background_white"
                    onClick={() => navigate('/login')}
                  />
                  <Button
                    text="Inscreva-se"
                    dimensions={{ width: '12rem', height: '2rem' }}
                    color="background_white"
                    backgroundHoverColor="secondary"
                    border={{ color: 'transparent', width: 1 }}
                    background="tertiary_500"
                    textHoverColor="background_white"
                    onClick={() => navigate('/register')}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="container">
            <div className="containerOptions">
              {routes
                .filter((route) => route.enabled !== false)
                .map(({ path, name, options }) => (
                  <Link
                    key={path}
                    to={path}
                    className={`nav-link ${path.includes(pathName) ||
                      options?.some((o) => o.path.includes(pathName) || activeOption === path)
                      ? 'active'
                      : ''
                      } ${options ? 'withSubOption' : ''}`}
                    onMouseEnter={() => setActiveOption(path)}>
                    {name}
                  </Link>
                ))}
              {/* <Link className="nav-link" to="" onClick={handleRedirectTimeSheet}>
                TimeSheet
              </Link> */}
              {subOptions && (
                <div className="containerPath" onMouseLeave={handleMouseLeaveContainerPath}>
                  <div className="contentPath">
                    <div className="path">
                      <div className="links">
                        {subOptions?.map((option) => (
                          <Link
                            key={option.path}
                            to={option.path}
                            onMouseEnter={() => setActiveSubOption(option.path)}>
                            {option.name}
                          </Link>
                        ))}
                      </div>
                      <div className={activeSubOption ? 'description' : 'displayNone'}>
                        <p>{subOptions?.find((o) => o.path === activeSubOption)?.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="containerProfile">
              <NotificationHeader />
              <OptionsProfile />
              <Button
                text=""
                LeftComponent={Logout}
                style={{ fontSize: '1.20rem', display: 'flex', alignItems: 'center' }}
                dimensions={{
                  width: '2rem',
                  height: '2rem',
                }}
                positionLeftElement={10}
                border={{ color: 'transparent', width: 0 }}
                color="background_white"
                textHoverColor="background_white"
                backgroundHoverColor="primary"
                onClick={handleLogout}
              />
            </div>
          </div>
        )}
        <LogoHub2 className="logoMobile" />
      </Content>
    </Container>
  );
}
